import type { MenuTypesWithProduct } from "@/types/pos";
import { Clock3, CookingPot, UtensilsCrossed, CheckCircle2, XCircle } from "lucide-react";

export const formatTime = (timeString: string) => {
  const [hours, minutes] = timeString?.split(":").map(Number);
  const date = new Date();
  date.setHours(hours, minutes);

  return date.toLocaleString("en-US", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: true
  });
};

export const getStatusColor = (status: string) => {
  switch (status) {
    case "pending":
      return "bg-yellow-100 text-yellow-800 border-yellow-200";
    case "preparing":
      return "bg-blue-100 text-blue-800 border-blue-200";
    case "ready":
      return "bg-green-100 text-green-800 border-green-200";
    case "delivered":
      return "bg-gray-100 text-gray-800 border-gray-200";
    case "cancelled":
      return "bg-red-100 text-red-800 border-red-200";
    default:
      return "bg-gray-100 text-gray-800 border-gray-200";
  }
};

function getUserCurrentTime() {
  const now = new Date().toLocaleString("en-US", {
    timeZone: "America/New_York",
  });
  const d = new Date(now);
  return d.toTimeString().slice(0, 8);
}

export const isMenuAvailable = (menu: MenuTypesWithProduct) => {
  const current = getUserCurrentTime();
  const start = menu.startTime;
  const end = menu.endTime;

  const toMinutes = (time: string) => {
    const [h, m] = time.split(":").map(Number);
    return h * 60 + m;
  };

  const currentMin = toMinutes(current);
  const startMin = toMinutes(start);
  const endMin = toMinutes(end);

  if (!menu.isNextDay && startMin < endMin) {
    return currentMin >= startMin && currentMin < endMin;
  }
  if (!menu.isNextDay && startMin > endMin) {
    return currentMin >= startMin || currentMin < endMin;
  }
  if (menu.isNextDay && startMin > endMin) {
    return currentMin >= startMin || currentMin < endMin;
  }
  if (menu.isNextDay && startMin < endMin) {
    return currentMin < endMin;
  }
  return false;
};

export const STATUS_STEPS = [
  { key: "pending", label: "Pending", Icon: Clock3, textColor: "text-yellow-500" },
  { key: "preparing", label: "Preparing", Icon: CookingPot, textColor: "text-blue-500" },
  { key: "ready", label: "Ready", Icon: UtensilsCrossed, textColor: "text-green-500" },
  { key: "delivered", label: "Delivered", Icon: CheckCircle2, textColor: "text-gray-400" },
];

export const CANCELLED_STATUS = { key: "cancelled", label: "Cancelled", Icon: XCircle, textColor: "text-red-500" };

export const STATUS_MAP = {
  ...Object.fromEntries(STATUS_STEPS.map(step => [step.key, step])),
  cancelled: CANCELLED_STATUS,
} as Record<string, typeof STATUS_STEPS[number]>;

export const shouldShowNextDayCheckbox = (
  startTime?: string,
  endTime?: string
) => {
  if (!startTime || !endTime) return false;
  const [sH, sM] = startTime.split(":").map(Number);
  const [eH, eM] = endTime.split(":").map(Number);
  if ([sH, sM, eH, eM].some((v) => isNaN(v))) return false;

  const isStartPM = sH >= 12;
  const isEndPM = eH >= 12;

  const startTotal = sH * 60 + sM;
  const endTotal = eH * 60 + eM;

  return isStartPM === isEndPM && endTotal < startTotal;
};

export const getStatusColorCode = (value: string) => {
  if (value === "active") return "bg-green-100 text-green-800 border-green-200";
  if (value === "cancelled") return "bg-red-100 text-red-800 border-red-200";
  return "bg-yellow-100 text-yellow-800 border-yellow-200";
};

export const getStatusDotColor = (value: string) => {
  if (value === "active") return "bg-green-800";
  return "bg-red-800";
};

export function getFileNameFromUrl(url?: string | null) {
  if (!url) return "";
  return url.split("/").pop() ?? "";
}

export function buildFormData(
  data: Record<string, any>,
  file?: File | null,
  fileKey: string = "image"
) {
  const formData = new FormData();

  Object.entries(data).forEach(([key, value]) => {
    if (value === undefined || value === null) return;
    if (Array.isArray(value) || typeof value === "object") {
      formData.append(key, JSON.stringify(value));
    } else {
      formData.append(key, String(value));
    }
  });

  if (file) {
    formData.append(fileKey, file);
  }

  return formData;
}
